import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from './Layout';
import * as XLSX from 'xlsx';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    message,
    Popconfirm,
    Tag,
    Row,
    Col,
    Switch,
    InputNumber,
    Upload,
    Alert,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CloseOutlined,
    SaveOutlined,
    DownloadOutlined,
    UploadOutlined,
} from '@ant-design/icons';

const { Option } = Select;

const TemplatesPage = () => {
    // --- Состояния ---
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [form] = Form.useForm();
    const [options, setOptions] = useState({
        scenarios: [],
        articles: [],
        nomenclatures: [],
    });
    const [filteredNomenclatures, setFilteredNomenclatures] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [nomenclatureSearch, setNomenclatureSearch] = useState('');

    // Состояния для импорта
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [importErrors, setImportErrors] = useState([]);
    const [fileList, setFileList] = useState([]);
    const [filterScenarioId, setFilterScenarioId] = useState(null);

    // --- Загрузка данных ---
    useEffect(() => {
        fetchTemplates();
        fetchOptions();
    }, [pagination.current, pagination.pageSize, searchText, filterScenarioId]);

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/templates', {
                params: {
                    page: pagination.current,
                    limit: pagination.pageSize,
                    search: searchText, // Поиск по контрагенту
                    scenarioId: filterScenarioId,
                },
            });
            setTemplates(response.data.data);
            setPagination(prev => ({
                ...prev,
                total: response.data.total,
            }));
        } catch (error) {
            message.error('Ошибка при загрузке шаблонов');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchOptions = async () => {
        try {
            const optionsResponse = await axios.get('/api/templates/options');

            setOptions({
                scenarios: optionsResponse.data.scenarios || [],
                articles: optionsResponse.data.articles || [],
                nomenclatures: optionsResponse.data.nomenclatures || []
            });
        } catch (error) {
            console.error('Ошибка при загрузке опций:', error);
            message.error('Ошибка при загрузке опций для формы');
        }
    };

    // --- Обработчики формы и фильтров ---
    const handleTypeChange = (type) => {
        form.setFieldsValue({ article_id: null, nomenclature_id: null });
        setFilteredNomenclatures([]);
        setNomenclatureSearch('');
    };

    const handleArticleChange = (articleId) => {
        // Очищаем номенклатуру только при ручном изменении статьи
        form.setFieldsValue({ nomenclature_id: null });
        const filtered = options.nomenclatures.filter(n => n.article_id === articleId);
        setFilteredNomenclatures(filtered);
        setNomenclatureSearch('');
    };

    const handleNomenclatureSearch = (value) => {
        setNomenclatureSearch(value);
    };

    const filteredNomenclatureOptions = useMemo(() => {
        if (!nomenclatureSearch) {
            return filteredNomenclatures;
        }
        return filteredNomenclatures.filter(item =>
            item.name.toLowerCase().includes(nomenclatureSearch.toLowerCase()) ||
            (item.unit && item.unit.toLowerCase().includes(nomenclatureSearch.toLowerCase()))
        );
    }, [filteredNomenclatures, nomenclatureSearch]);

    const groupedArticles = useMemo(() => {
        return {
            income: options.articles.filter(a => a.name === '1. Доход'),
            expense: options.articles.filter(a => a.name !== '1. Доход'),
        };
    }, [options.articles]);

    // --- CRUD операции ---
    const showModal = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            const article = options.articles.find(a => a.name === template.article_id);
            const nomenclature = options.nomenclatures.find(n => n.name === template.nomenclature_id);

            // Сначала фильтруем список номенклатур для текущей статьи
            if (article) {
                const filtered = options.nomenclatures.filter(n => n.article_id === article.id);
                setFilteredNomenclatures(filtered);
            } else {
                setFilteredNomenclatures([]);
            }

            // Устанавливаем значения формы (без вызова handleArticleChange, чтобы не занулить nomenclature_id)
            form.setFieldsValue({
                ...template,
                scenario_ids: template.scenario_ids || [],
                article_id: article ? article.id : null,
                nomenclature_id: nomenclature ? nomenclature.id : null,
            });

        } else {
            setEditingTemplate(null);
            form.resetFields();
            form.setFieldsValue({ is_active: true, scenario_ids: [] });
            setFilteredNomenclatures([]);
            setNomenclatureSearch('');
        }
        setIsModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Преобразуем ID обратно в названия для отправки на бэкенд,
            // если ваш API ожидает строки-названия (как указано в логике поиска имен в файле)
            const articleObj = options.articles.find(a => a.id === values.article_id);
            const nomenclatureObj = options.nomenclatures.find(n => n.id === values.nomenclature_id);

            const payload = {
                ...values,
                article_id: articleObj ? articleObj.name : values.article_id,
                nomenclature_id: nomenclatureObj ? nomenclatureObj.name : values.nomenclature_id,
            };

            if (editingTemplate) {
                await axios.put(`/api/templates/${editingTemplate.id}`, payload);
                message.success('Шаблон успешно обновлен');
            } else {
                await axios.post('/api/templates', payload);
                message.success('Шаблон успешно создан');
            }
            setIsModalVisible(false);
            fetchTemplates();
        } catch (error) {
            message.error(error.response?.data?.message || 'Ошибка при сохранении шаблона');
            console.error('Ошибка при сохранении шаблона:', error);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`/api/templates/${id}`);
            message.success('Шаблон успешно удален');
            fetchTemplates();
        } catch (error) {
            message.error('Ошибка при удалении шаблона');
            console.error(error);
        }
    };

    // --- Экспорт и Импорт ---

    const handleExport = async () => {
        const key = 'exporting';
        message.loading({ content: 'Получение данных для экспорта...', key });

        try {
            const response = await axios.get('/api/templates/all');
            const templatesToExport = response.data;

            if (!templatesToExport || templatesToExport.length === 0) {
                message.warn({ content: 'Нет данных для экспорта.', key, duration: 2 });
                return;
            }

            message.loading({ content: 'Формирование файла...', key });

            const dataForSheet = templatesToExport.map(t => ({
                'ID Шаблона': t.id,
                'Сценарий': t.scenario_name || 'Не указан',
                'Тип': t.type,
                'Статья': t.article_id,
                'Номенклатура': t.nomenclature_id || '',
                'Контрагент': t.counterparty || '',
                'Процент': t.percentage,
                'Количество': t.quantity,
                'Штраф': t.penalty,
                'Учитывать': t.is_active ? 'Да' : 'Нет',
                'Удалить': 'Нет',
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(dataForSheet);
            XLSX.utils.book_append_sheet(wb, ws, "Шаблоны");

            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'templates-export.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            message.success({ content: 'Файл успешно экспортирован!', key, duration: 2 });

        } catch (error) {
            console.error('Ошибка при экспорте:', error);
            message.error({ content: error.response?.data?.message || 'Не удалось экспортировать данные.', key, duration: 3 });
        }
    };


    const showImportModal = () => {
        setImportErrors([]);
        setFileList([]);
        setIsImportModalVisible(true);
    };

    const handleImportCancel = () => {
        setIsImportModalVisible(false);
    };

    const uploadProps = {
        name: 'file',
        accept: '.xlsx, .xls',
        fileList: fileList,
        beforeUpload: (file) => {
            const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
            if (!isExcel) {
                message.error(`${file.name} не является Excel файлом.`);
            }
            setFileList([file]);
            setImportErrors([]);
            return false;
        },
        onRemove: () => {
            setFileList([]);
        }
    };

    const readExcelFile = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(firstSheet);
                    resolve(jsonData);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    };

    const handleUpload = async () => {
        if (fileList.length === 0) {
            message.error('Пожалуйста, выберите файл для загрузки.');
            return;
        }

        const key = 'uploading';
        message.loading({ content: 'Импорт файла...', key });

        try {
            const fileData = await readExcelFile(fileList[0]);

            const response = await axios.post('/api/templates/import', fileData, {
                headers: { 'Content-Type': 'application/json' },
            });

            message.success({ content: response.data.message || 'Файл успешно импортирован.', key });
            setIsImportModalVisible(false);
            fetchTemplates();
        } catch (error) {
            message.error({ content: 'Ошибка при импорте файла.', key });
            console.error('Import error:', error);
            if (error.response?.data?.errors) {
                setImportErrors(error.response.data.errors.map(e => `Строка ${e.row}: ${e.error}`));
            } else if (error.response?.data?.message) {
                setImportErrors([error.response.data.message]);
            } else {
                setImportErrors(['Неизвестная ошибка при импорте. Проверьте консоль.']);
            }
        }
    };


    // --- Колонки таблицы ---
    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 80, sorter: true },
        {
            title: 'Сценарии',
            dataIndex: 'scenarios',
            key: 'scenarios',
            render: (scenarios) => scenarios && scenarios.length > 0
                ? scenarios.map(s => <Tag key={s.id}>{s.scenario_name}</Tag>)
                : 'Не указан',
        },
        {
            title: 'Тип', dataIndex: 'type', key: 'type',
            render: (type) => (
                <Tag color={type === 'income' ? 'green' : 'red'}>
                    {type === 'income' ? 'Доход' : 'Расход'}
                </Tag>
            ),
        },
        { title: 'Статья', dataIndex: 'article_id', key: 'article' },
        { title: 'Номенклатура', dataIndex: 'nomenclature_id', key: 'nomenclature', render: (value) => value || '-' },
        { title: 'Контрагент', dataIndex: 'counterparty', key: 'counterparty', render: (value) => value || '-' },
        { title: 'Процент', dataIndex: 'percentage', key: 'percentage', render: (value) => `${value}%` },
        { title: 'Кол-во', dataIndex: 'quantity', key: 'quantity' },
        { title: 'Штраф', dataIndex: 'penalty', key: 'penalty' },
        {
            title: 'Активен', dataIndex: 'is_active', key: 'is_active',
            render: (value) => (<Tag color={value ? 'green' : 'red'}>{value ? 'Да' : 'Нет'}</Tag>),
        },
        {
            title: 'Действия', key: 'actions', width: 120, fixed: 'right',
            render: (_, record) => (
                <Space size="middle">
                    <Button type="link" icon={<EditOutlined />} onClick={() => showModal(record)} />
                    <Popconfirm title="Удалить шаблон?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
                        <Button type="link" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Layout>
            <div style={{ padding: 24 }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <h2>Управление шаблонами</h2>
                    </Col>
                    <Col>
                        <Space>
                            {/* НОВЫЙ ФИЛЬТР ПО СЦЕНАРИЮ */}
                            <Select
                                placeholder="Фильтр по сценарию"
                                style={{ width: 200 }}
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                value={filterScenarioId}
                                onChange={(val) => {
                                    setFilterScenarioId(val);
                                    setPagination(prev => ({ ...prev, current: 1 }));
                                }}
                            >
                                {options.scenarios.map(s => (
                                    <Option key={s.id} value={s.id}>{s.scenario_name}</Option>
                                ))}
                            </Select>

                            <Input.Search
                                placeholder="Поиск по номенклатуре"
                                allowClear
                                enterButton
                                onSearch={(value) => {
                                    setSearchText(value);
                                    setPagination(prev => ({ ...prev, current: 1 }));
                                }}
                                style={{ width: 280 }}
                            />
                            <Button icon={<DownloadOutlined />} onClick={handleExport}>Экспорт</Button>
                            <Button icon={<UploadOutlined />} onClick={showImportModal}>Импорт</Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>Добавить</Button>
                        </Space>
                       
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={templates}
                    rowKey="id"
                    loading={loading}
                    pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ['10', '50', '100', '500'] // Позволит выбирать большой размер страницы
    }}
                    onChange={(p) => setPagination(p)}
                    bordered
                    scroll={{ x: 1300 }}
                />

                <Modal
                    title="Импорт шаблонов из Excel"
                    visible={isImportModalVisible}
                    onCancel={handleImportCancel}
                    footer={[
                        <Button key="back" onClick={handleImportCancel}>Отмена</Button>,
                        <Button key="submit" type="primary" onClick={handleUpload} disabled={fileList.length === 0}>
                            Загрузить
                        </Button>,
                    ]}
                >
                    <p>Выберите XLSX-файл для импорта. Заголовки столбцов должны соответствовать экспортируемому файлу.</p>
                    <p>Для поля "Сценарий" укажите несколько названий, разделенных запятыми (например: <Tag>Сценарий 1, Сценарий 2</Tag>).</p>
                    <Upload {...uploadProps}>
                        <Button icon={<UploadOutlined/>}>Выбрать файл</Button>
                    </Upload>
                    {importErrors.length > 0 && (
                        <Alert
                            message="Ошибки при импорте"
                            description={
                                <ul style={{paddingLeft: 20, maxHeight: 200, overflowY: 'auto', margin: 0}}>
                                    {importErrors.map((error, index) => <li key={index}>{error}</li>)}
                                </ul>
                            }
                            type="error"
                            showIcon
                            style={{marginTop: 16}}
                        />
                    )}
                </Modal>

                <Modal
                    title={editingTemplate ? 'Редактировать шаблон' : 'Добавить новый шаблон'}
                    visible={isModalVisible}
                    onOk={handleSubmit}
                    onCancel={() => setIsModalVisible(false)}
                    width={800}
                    footer={[
                        <Button key="back" onClick={() => setIsModalVisible(false)} icon={<CloseOutlined />}>Отмена</Button>,
                        <Button key="submit" type="primary" onClick={handleSubmit} icon={<SaveOutlined />}>Сохранить</Button>,
                    ]}
                >
                    <Form form={form} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="scenario_ids" label="Сценарии" rules={[{ required: true, message: 'Выберите хотя бы один сценарий' }]}>
                                    <Select
                                        mode="multiple"
                                        placeholder="Выберите сценарии"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {options.scenarios.map(s => <Option key={s.id} value={s.id}>{s.scenario_name}</Option>)}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="type" label="Тип" rules={[{ required: true, message: 'Выберите тип' }]}>
                                    <Select placeholder="Выберите тип" onChange={handleTypeChange}>
                                        <Option value="income">Доход</Option>
                                        <Option value="expense">Расход</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="article_id" label="Статья" rules={[{ required: true, message: 'Выберите статью' }]}>
                                    <Select placeholder="Выберите статью" onChange={handleArticleChange} showSearch optionFilterProp="children">
                                        {form.getFieldValue('type') === 'income' ?
                                            groupedArticles.income.map(a => <Option key={a.id} value={a.id}>{a.name}</Option>) :
                                            groupedArticles.expense.map(a => <Option key={a.id} value={a.id}>{a.name}</Option>)}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="nomenclature_id" label="Номенклатура">
                                    <Select
                                        placeholder="Выберите номенклатуру"
                                        showSearch
                                        onSearch={handleNomenclatureSearch}
                                        filterOption={false}
                                        disabled={!form.getFieldValue('article_id')}
                                    >
                                        {filteredNomenclatureOptions.map(item => (
                                            <Option key={item.id} value={item.id}>{item.name} {item.unit && `(${item.unit})`}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="counterparty" label="Контрагент">
                                    <Input placeholder="Введите название контрагента" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="is_active" label="Учитывать" valuePropName="checked">
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="percentage" label="Процент (%)" rules={[{ required: true, message: 'Введите процент' }]}>
                                    <InputNumber min={0} max={1000} style={{ width: '100%' }} formatter={v => `${v}%`} parser={v => v.replace('%', '')} />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="quantity" label="Количество" rules={[{ required: true, message: 'Введите количество' }]}>
                                    <Input placeholder="Введите количество или переменную" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="penalty" label="Штраф" initialValue={0}>
                                    <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TemplatesPage;